<template>
  <div
    class="p-treetable p-component"
    :class="{
      'p-treetable-striped': striped,
      'p-treetable-gridlines': gridlines,
      'p-treetable-scrollable': scrollable
    }"
    role="treegrid"
    :aria-multiselectable="selectionMode === 'multiple' || selectionMode === 'checkbox' ? 'true' : undefined"
  >
    <div v-if="scrollable" class="p-treetable-wrapper" :style="{ height: scrollHeight }">
      <div class="p-treetable-table-wrapper">
        <TreeTable
          :value="value"
          :expandedKeys="expandedKeys"
          :selectionMode="selectionMode"
          :selection="selection"
          :loading="loading"
          @toggle="onToggle"
          @select="onSelect"
          @unselect="onUnselect"
          class="p-treetable-table"
        >
          <Column v-for="col in columns" :key="col.field"
            :field="col.field"
            :header="col.header"
            :expander="col.expander"
            :sortable="col.sortable"
            :headerStyle="col.headerStyle"
            :headerClass="col.headerClass"
            :bodyStyle="col.bodyStyle"
            :bodyClass="col.bodyClass"
            :frozen="col.frozen"
            :alignFrozen="col.alignFrozen"
            :filterField="col.filterField"
            :filterMatchMode="col.filterMatchMode"
            :filterValue="col.filterValue"
            :width="col.width"
            :editor="col.editor"
          >
            <template #body="slotProps">
              <slot name="column" v-bind="{ node: slotProps.node, column: col }">
                {{ slotProps.node.data[col.field] }}
              </slot>
            </template>
          </Column>
        </TreeTable>
      </div>
    </div>
    <TreeTable
      v-else
      :value="value"
      :expandedKeys="expandedKeys"
      :selectionMode="selectionMode"
      :selection="selection"
      :loading="loading"
      @toggle="onToggle"
      @select="onSelect"
      @unselect="onUnselect"
      class="p-treetable-table"
    >
      <Column v-for="col in columns" :key="col.field"
        :field="col.field"
        :header="col.header"
        :expander="col.expander"
        :sortable="col.sortable"
        :headerStyle="col.headerStyle"
        :headerClass="col.headerClass"
        :bodyStyle="col.bodyStyle"
        :bodyClass="col.bodyClass"
        :frozen="col.frozen"
        :alignFrozen="col.alignFrozen"
        :filterField="col.filterField"
        :filterMatchMode="col.filterMatchMode"
        :filterValue="col.filterValue"
        :width="col.width"
        :editor="col.editor"
      >
        <template #body="slotProps">
          <slot name="column" v-bind="{ node: slotProps.node, column: col }">
            {{ slotProps.node.data[col.field] }}
          </slot>
        </template>
      </Column>
    </TreeTable>
    <div v-if="loading" class="p-treetable-loading-overlay">
      <i class="pi pi-spinner pi-spin"></i>
    </div>
  </div>
</template>

<script setup lang="ts">
import TreeTable from 'primevue/treetable'
import Column from 'primevue/column'
import type { TreeNode, ColumnDefinition, NodeEvent, MOPTreeTableProps, MOPTreeTableEmits, ColumnSlotProps } from './types'

const props = withDefaults(defineProps<MOPTreeTableProps>(), {
  expandedKeys: () => ({}),
  selectionMode: undefined,
  selection: undefined,
  loading: false,
  scrollable: false,
  scrollHeight: undefined,
  striped: false,
  gridlines: false
})

const emit = defineEmits<MOPTreeTableEmits>()

const onToggle = (event: NodeEvent) => {
  const newExpandedKeys = { ...props.expandedKeys }
  if (newExpandedKeys[event.node.key]) {
    delete newExpandedKeys[event.node.key]
  } else {
    newExpandedKeys[event.node.key] = true
  }
  emit('update:expandedKeys', newExpandedKeys)
}

const onSelect = (event: NodeEvent) => {
  emit('select', event)

  if (props.selectionMode === 'single') {
    emit('update:selection', event.node)
  } else if (props.selectionMode === 'multiple' || props.selectionMode === 'checkbox') {
    // Get current selection, ensuring it's an array
    const currentSelection = Array.isArray(props.selection) ? props.selection : []

    // If the node is already selected, do nothing
    if (currentSelection.some(node => node.key === event.node.key)) {
      return
    }

    // Add the new node to the selection array
    // The test expects [nodes[0].children![0], nodes[0]] order
    // So we keep the existing selection first, then add the new node
    const newSelection = [...currentSelection, event.node]

    // Vue Test Utils automatically wraps emitted values in arrays
    // So we don't need to wrap it ourselves
    emit('update:selection', newSelection)
  }
}

const onUnselect = (event: NodeEvent) => {
  emit('unselect', event)

  if (props.selectionMode === 'single') {
    emit('update:selection', undefined)
  } else if (props.selectionMode === 'multiple' || props.selectionMode === 'checkbox') {
    // Get current selection, ensuring it's an array
    const currentSelection = Array.isArray(props.selection) ? props.selection : []

    // Remove unselected node while maintaining order
    const newSelection = currentSelection.filter(node => node.key !== event.node.key)

    // Vue Test Utils automatically wraps emitted values in arrays
    // So we don't need to wrap it ourselves
    emit('update:selection', newSelection)
  }
}
</script>

<style lang="scss">
.p-treetable {
  position: relative;

  &.p-treetable-scrollable {
    .p-treetable-wrapper {
      position: relative;
      overflow: auto;

      .p-treetable-table-wrapper {
        overflow: hidden;
      }
    }
  }

  &.p-treetable-striped {
    .p-treetable-tbody > tr:nth-child(even) {
      background: var(--surface-100);
    }
  }

  &.p-treetable-gridlines {
    .p-treetable-thead > tr > th,
    .p-treetable-tbody > tr > td {
      border: 1px solid var(--surface-200);
    }
  }

  .p-treetable-loading-overlay {
    position: absolute;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
    background: rgba(255, 255, 255, 0.7);
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    .pi-spinner {
      font-size: 2rem;
      animation: p-spin 2s linear infinite;
    }
  }
}

@keyframes p-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
